import { CustomDataAdaptor } from '@syncfusion/ej2-data';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { fakeHttpRequest } from '../fakeHttpRequest';
import { IDynamicAutocompleteItem } from './dynamic-autocomplete-item';

/**
 * Custom adaptor that extends the autocomplete data loading to include an initial item.
 * When the search text is empty, the initial item is returned so the autocomplete
 * can resolve the pre-set value to a display text without hitting the server.
 */
export class DynamicAutocompleteInitialItemAdaptor extends CustomDataAdaptor {
  private searchSubject = new Subject<{ searchText: string; args: any }>();

  constructor(
    private getDataFunction: (searchText: string) => Observable<string[] | IDynamicAutocompleteItem[]>,
    private initialItem: IDynamicAutocompleteItem
  ) {
    super();
    this.options.getData = this.getData.bind(this);

    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged((prev, curr) => prev.searchText === curr.searchText),
      switchMap(({ searchText, args }) =>
        this.getDataFunction(searchText).pipe(
          switchMap(result => {
            args.onSuccess(result, { httpRequest: new fakeHttpRequest() });
            return [];
          }),
        )
      ),
    ).subscribe({
      error: () => {},
    });
  }

  private getData(args: any) {
    const data = JSON.parse(args.data);
    if (this.getDataFunction == null) {
      args.onFailure('getDataFunction is null');
      return;
    }

    const searchText = data.where ? data.where[0].value : null;

    // When no search text, return the initial item so the autocomplete can resolve the value
    if (!searchText && this.initialItem) {
      args.onSuccess([this.initialItem], { httpRequest: new fakeHttpRequest() });
      return;
    }

    this.searchSubject.next({ searchText, args });
  }
}
